import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api, { formatApiErrorDetail } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch (e) {
      // If auth is stale/invalid, purge any leftover client-side token so a
      // subsequent register/login writes a clean session (prevents the "old
      // cookie shadows fresh signup" bug reported 2026-07-16).
      if (e?.response?.status === 401) {
        localStorage.removeItem('clanster_access_token');
        sessionStorage.removeItem('clanster_access_token');
      }
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const register = useCallback(async ({ code, email, password, turnstile_token }) => {
    const { data } = await api.post('/auth/register', { code, email, password, turnstile_token });
    if (data.access_token) {
      localStorage.setItem('clanster_access_token', data.access_token);
    }
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async ({ email, password, remember = true }) => {
    const { data } = await api.post('/auth/login', { email, password, remember });
    if (data.access_token) {
      // Persistent for Remember-me=on (30-day cookie backup), otherwise
      // session-scoped so the token dies when the tab closes — matches the
      // session-only cookie we get back from the backend when remember=false.
      if (remember) {
        localStorage.setItem('clanster_access_token', data.access_token);
        sessionStorage.removeItem('clanster_access_token');
      } else {
        sessionStorage.setItem('clanster_access_token', data.access_token);
        localStorage.removeItem('clanster_access_token');
      }
    }
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {}
    localStorage.removeItem('clanster_access_token');
    sessionStorage.removeItem('clanster_access_token');
    setUser(false);
  }, []);

  // Memoize the context value so consumers don't re-render on every parent
  // re-render — only when user identity or callback identities actually change.
  const contextValue = useMemo(
    () => ({ user, setUser, register, login, logout, refreshMe, formatApiErrorDetail }),
    [user, register, login, logout, refreshMe]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
